import { useState, useCallback, useRef } from 'react';
import { CandidateSummary, CandidateStatus, ALLOWED_FILE_TYPES, MAX_FILE_SIZE, MAX_BATCH_SIZE } from '@recruitai/shared';
import { apiClient } from '../lib/api-client';
import { supportsDirectoryPicker, collectFilesFromDirectory, filterByExtension } from '../lib/folder-utils';
import { uploadWithConcurrency, UploadTask } from '../lib/upload-utils';

export interface FolderFile {
  file: File;
  id: string;
  status: 'valid' | 'invalid' | 'pending' | 'uploading' | 'uploaded' | 'error';
  progress: number;
  error?: string;
  key?: string;
}

interface BatchUploadResponse {
  candidates: Array<{ candidateId: string; name: string; status: 'pending' }>;
}

const MAX_CONCURRENCY = 5;

export function useFolderUpload(jobId: string) {
  const [folderFiles, setFolderFiles] = useState<FolderFile[]>([]);
  const [isSelecting, setIsSelecting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [truncatedCount, setTruncatedCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const folderInputRef = useRef<HTMLInputElement | null>(null);

  const processFiles = useCallback((rawFiles: File[]) => {
    const filtered = filterByExtension(rawFiles);
    if (filtered.length === 0) {
      setError('Nenhum currículo encontrado na pasta selecionada. Formatos aceitos: PDF, DOC, DOCX.');
      setFolderFiles([]);
      setTruncatedCount(0);
      return;
    }

    const truncated = Math.max(0, filtered.length - MAX_BATCH_SIZE);
    setTruncatedCount(truncated);
    const toProcess = filtered.slice(0, MAX_BATCH_SIZE);

    const items: FolderFile[] = toProcess.map(file => {
      const mimeOk = (ALLOWED_FILE_TYPES as readonly string[]).includes(file.type);
      const sizeOk = file.size <= MAX_FILE_SIZE;
      let status: FolderFile['status'] = 'valid';
      let err: string | undefined;
      if (!mimeOk) { status = 'invalid'; err = 'Formato não suportado'; }
      else if (!sizeOk) { status = 'invalid'; err = 'Arquivo excede o limite de 10MB'; }
      return { file, id: crypto.randomUUID(), status, progress: 0, error: err };
    });

    setFolderFiles(items);
    setError(null);
  }, []);

  const selectFolder = useCallback(async () => {
    setIsSelecting(true);
    setError(null);
    try {
      if (supportsDirectoryPicker()) {
        const dirHandle = await (window as any).showDirectoryPicker();
        const files = await collectFilesFromDirectory(dirHandle);
        processFiles(files);
      } else if (folderInputRef.current) {
        folderInputRef.current.click();
      }
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'Falha ao selecionar pasta');
      }
    } finally {
      setIsSelecting(false);
    }
  }, [processFiles]);

  const handleFolderInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      processFiles(Array.from(e.target.files));
      e.target.value = '';
    }
  }, [processFiles]);

  const startUpload = useCallback(async (): Promise<CandidateSummary[]> => {
    const validFiles = folderFiles.filter(f => f.status === 'valid' || f.status === 'pending');
    if (validFiles.length === 0) return [];

    setIsUploading(true);
    setError(null);
    const controller = new AbortController();
    abortRef.current = controller;

    // Transition valid → pending
    setFolderFiles(prev => prev.map(f =>
      f.status === 'valid' ? { ...f, status: 'pending' as const } : f
    ));

    const tasks: UploadTask[] = validFiles.map(f => ({ id: f.id, file: f.file }));

    const results = await uploadWithConcurrency(
      tasks, jobId, MAX_CONCURRENCY,
      (id, progress) => setFolderFiles(prev => prev.map(f =>
        f.id === id ? { ...f, status: 'uploading' as const, progress } : f
      )),
      (id, key) => setFolderFiles(prev => prev.map(f =>
        f.id === id ? { ...f, status: 'uploaded' as const, progress: 100, key } : f
      )),
      (id, err) => setFolderFiles(prev => prev.map(f =>
        f.id === id ? { ...f, status: 'error' as const, progress: 0, error: err } : f
      )),
      controller.signal
    );

    const successful = results.filter(r => r.success);
    if (successful.length === 0) {
      setError('Nenhum arquivo foi enviado com sucesso');
      setIsUploading(false);
      return [];
    }

    try {
      const candidates = successful.map(r => {
        const ff = validFiles.find(f => f.id === r.id)!;
        return {
          name: ff.file.name.replace(/\.[^.]+$/, ''),
          email: '',
          resumeS3Key: r.key,
          fileName: ff.file.name,
        };
      });

      const batchResponse = await apiClient.post<BatchUploadResponse>(
        `/jobs/${jobId}/candidates/batch-upload`,
        { candidates }
      );

      setIsUploading(false);
      return batchResponse.candidates.map(c => ({
        candidateId: c.candidateId,
        name: c.name,
        email: '',
        status: CandidateStatus.PENDING,
        fitScore: 0,
        uploadedAt: new Date().toISOString(),
      }));
    } catch (err: any) {
      setError(err.message || 'Falha ao registrar candidatos');
      setIsUploading(false);
      return [];
    }
  }, [folderFiles, jobId]);

  const retryFile = useCallback((id: string) => {
    setFolderFiles(prev => prev.map(f =>
      f.id === id && f.status === 'error'
        ? { ...f, status: 'valid' as const, progress: 0, error: undefined }
        : f
    ));
  }, []);

  const cancelUpload = useCallback(() => {
    abortRef.current?.abort();
    setFolderFiles(prev => prev.map(f =>
      f.status === 'pending' || f.status === 'uploading'
        ? { ...f, status: 'valid' as const, progress: 0 }
        : f
    ));
    setIsUploading(false);
  }, []);

  const clearFiles = useCallback(() => {
    setFolderFiles([]);
    setTruncatedCount(0);
    setError(null);
  }, []);

  const overallProgress = {
    completed: folderFiles.filter(f => f.status === 'uploaded').length,
    total: folderFiles.filter(f => f.status !== 'invalid').length,
    errors: folderFiles.filter(f => f.status === 'error').length,
  };

  return {
    folderFiles, isSelecting, isUploading, overallProgress, truncatedCount, error,
    selectFolder, handleFolderInput, startUpload, retryFile, cancelUpload, clearFiles,
    folderInputRef,
  };
}
